<template>
    <div class="modal fade photo-lightbox" tabindex="-1" aria-label="Zvětšený obrázek" ref="modal" @keydown="onKeydown">
        <div class="modal-dialog modal-xl modal-fullscreen-lg-down modal-dialog-centered">
            <div class="modal-content border-0" v-if="current !== null">
                <div class="modal-header border-0">
                    <span class="text-white-50">{{ index + 1 }} z {{ items.length }}</span>
                    <span v-if="titleBadge && index === 0" class="photo-title-badge ms-2">
                        <span class="fas fa-star"></span>Titulní fotka
                    </span>
                    <button type="button" class="btn-close btn-close-white ms-auto" data-bs-dismiss="modal" aria-label="Zavřít"></button>
                </div>
                <div class="modal-body pt-0">
                    <div class="photo-lightbox-stage">
                        <img :src="current.url" :alt="current.description">
                        <div v-if="rotating" class="photo-tile-busy">
                            <span class="spinner-border text-light" role="status"></span>
                        </div>
                        <template v-if="items.length > 1">
                            <button type="button" class="btn btn-light photo-lightbox-previous" title="Předchozí (šipka vlevo)" @click="step(-1)">
                                <span class="fas fa-chevron-left"></span>
                            </button>
                            <button type="button" class="btn btn-light photo-lightbox-next" title="Další (šipka vpravo)" @click="step(1)">
                                <span class="fas fa-chevron-right"></span>
                            </button>
                        </template>
                    </div>
                </div>
                <div class="modal-footer border-0 justify-content-start" v-if="canDescribe || canRotate">
                    <input v-if="canDescribe"
                           type="text"
                           class="form-control form-control-sm bg-dark text-white border-secondary w-auto flex-grow-1"
                           v-model="description"
                           placeholder="Přidat popisek…"
                           aria-label="Popisek fotografie"
                           @change="$emit('describe', current.id, description.trim())"
                           @keydown.enter="($event.target as HTMLInputElement).blur()"
                    >
                    <div v-if="canRotate" class="ms-auto d-flex gap-2">
                        <button type="button" class="btn btn-sm btn-outline-light" :disabled="rotating" @click="$emit('rotate', current.id, 90)">
                            <span class="fas fa-undo me-1"></span> Otočit doleva
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-light" :disabled="rotating" @click="$emit('rotate', current.id, -90)">
                            <span class="fas fa-redo me-1"></span> Otočit doprava
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {Modal} from 'bootstrap';
import {computed, defineComponent, onMounted, PropType, ref, watch} from 'vue';

export interface LightboxItem {
    id: string;
    url: string;
    description: string;
}

export default defineComponent({
    props: {
        items: {type: Array as PropType<LightboxItem[]>, required: true},
        titleBadge: {type: Boolean, required: true},
        canDescribe: {type: Boolean, required: true},
        canRotate: {type: Boolean, required: true},
        rotatingIds: {type: Array as PropType<string[]>, required: true},
    },

    emits: ['describe', 'rotate'],

    setup(props, {expose}) {
        const modal = ref<HTMLElement | null>(null);
        const index = ref(0);
        const description = ref('');
        let modalInstance: Modal | null = null;

        const current = computed(() => props.items[index.value] ?? null);
        const rotating = computed(() => current.value !== null && props.rotatingIds.includes(current.value.id));

        watch(current, item => {
            description.value = item?.description ?? '';
        }, {immediate: true});

        function open(itemIndex: number): void {
            index.value = itemIndex;
            modalInstance?.show();
        }

        function step(direction: number): void {
            index.value = (index.value + direction + props.items.length) % props.items.length;
        }

        function onKeydown(event: KeyboardEvent): void {
            if (event.target instanceof HTMLInputElement) {
                return;
            }

            if (event.key === 'ArrowLeft') {
                step(-1);
            } else if (event.key === 'ArrowRight') {
                step(1);
            }
        }

        onMounted(() => {
            modalInstance = new Modal(modal.value as HTMLElement);
        });

        expose({open});

        return {modal, index, description, current, rotating, open, step, onKeydown};
    },
});
</script>
